import React, { ReactNode } from 'react';

import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';

import { ComponentLink } from 'src/components/component-link';
import { useAnalytics } from 'src/components/settings/analytics';
import { useIntl } from 'src/hooks/intl';
import WorkspaceIcon from 'src/sections/workspace/components/workspace-icon';
import { getCurrentProject } from 'src/selectors/project-selectors';

import {
  getProjectWorkspaceName,
  getProjectWorkspaceUrl,
  getWorkspaceProjectsUrl,
} from '../utils/utils';

const messages = defineMessages({
  projectsBreadCrumb: {
    id: 'project.breadcrumbs.workspace.projects',
    description:
      'Text for breadcrumb item pointing to the workspace projects page',
    defaultMessage: 'Projects',
  },
});

/* eslint @typescript-eslint/ban-types: "warn" */
const ProjectBreadcrumbs: React.FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const intl = useIntl();
  const project = useSelector(getCurrentProject);
  const { publishBreadcrumbClickEvent } = useAnalytics();

  if (!project) {
    return null;
  }

  let projectHref: string | undefined = undefined;
  if (project!.links) {
    projectHref = project!.links!.html.href;
  }

  const workspaceIcon = <WorkspaceIcon workspace={project.workspace} />;

  return (
    <Breadcrumbs>
      <BreadcrumbsItem
        component={ComponentLink}
        href={getProjectWorkspaceUrl(project)}
        text={getProjectWorkspaceName(project) || ''}
        onClick={() => publishBreadcrumbClickEvent('WorkspaceLink')}
        iconBefore={workspaceIcon}
      />
      <BreadcrumbsItem
        component={ComponentLink}
        href={getWorkspaceProjectsUrl(project)}
        text={intl.formatMessage(messages.projectsBreadCrumb)}
        onClick={() => publishBreadcrumbClickEvent('ProjectsIndexLink')}
      />
      <BreadcrumbsItem
        component={ComponentLink}
        href={projectHref}
        text={project.name}
        onClick={() => publishBreadcrumbClickEvent('ProjectLink')}
      />
      {children}
    </Breadcrumbs>
  );
};

export default ProjectBreadcrumbs;
